import { createSlice, createEntityAdapter } from "@reduxjs/toolkit";

import {
  doGetAllOrders,
  doDeleteOrder,
  doCreateOrder,
  doCreateMutiOrders,
  doUploadOrders,
  doUpdateOrder,
  doDetailOrder,
  doDetailTrackingOrder,
  doDetailPublicOrder,
  doBulkDeleteOrders,
  doBulkUpdateOrders,
  doBulkUpdateDeliveryDate,
} from "./thunk";

const isActionWithPending = action => {
  return action.type.startsWith("orders/") && action.type.endsWith("/pending");
};

const isActionWithFulfilled = action => {
  return action.type.startsWith("orders/") && action.type.endsWith("/fulfilled");
};

const isActionWithFailed = action => {
  return action.type.startsWith("orders/") && action.type.endsWith("/rejected");
};

const ordersAdapter = createEntityAdapter({
  selectId: item => item._id,
  sortComparer: (a, b) => b.createdAt.localeCompare(a.createdAt),
});

const orderSlice = createSlice({
  name: "order",
  initialState: {
    action: null,
    isFetching: false,
    status: "idle",
    metadata: {},
    entities: [],
    ids: [],
    item: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder.addCase(doGetAllOrders.pending, state => {
      state.action = "getall";
      state.status = "loading";
    });
    builder.addCase(doGetAllOrders.fulfilled, (state, action) => {
      const { orders, meta } = action.payload;
      state.status = "succeeded";
      state.metadata = meta;
      ordersAdapter.setAll(state, orders);
    });
    builder.addCase(doGetAllOrders.rejected, state => {
      state.status = "failed";
    });

    builder.addCase(doCreateOrder.pending, state => {
      state.action = "create";
      state.status = "loading";
    });
    builder.addCase(doCreateOrder.fulfilled, (state, action) => {
      state.status = "succeeded";
      ordersAdapter.upsertOne(state, action.payload);
    });
    builder.addCase(doCreateOrder.rejected, state => {
      state.status = "failed";
    });

    builder.addCase(doCreateMutiOrders.pending, state => {
      state.action = "create-muti";
      state.status = "loading";
    });
    builder.addCase(doCreateMutiOrders.fulfilled, (state, action) => {
      state.status = "succeeded";
      ordersAdapter.upsertMany(state, action.payload);
    });
    builder.addCase(doCreateMutiOrders.rejected, state => {
      state.status = "failed";
    });

    builder.addCase(doUploadOrders.pending, state => {
      state.action = "upload";
      state.status = "loading";
    });
    builder.addCase(doUploadOrders.fulfilled, (state, action) => {
      state.status = "succeeded";
      state.orders = action.payload;
    });
    builder.addCase(doUploadOrders.rejected, state => {
      state.status = "failed";
    });

    builder.addCase(doDeleteOrder.pending, state => {
      state.action = "delete";
      state.status = "loading";
    });
    builder.addCase(doDeleteOrder.fulfilled, (state, action) => {
      state.status = "succeeded";
      ordersAdapter.removeOne(state, action.meta.arg);
    });
    builder.addCase(doDeleteOrder.rejected, state => {
      state.status = "failed";
    });

    builder.addCase(doUpdateOrder.pending, state => {
      state.action = "update";
    });
    builder.addCase(doUpdateOrder.fulfilled, (state, action) => {
      state.status = "succeeded";
      state.message = "update_succeeded";
      state.item = action.payload;
    });
    builder.addCase(doUpdateOrder.rejected, state => {
      state.status = "failed";
    });

    builder.addCase(doBulkDeleteOrders.fulfilled, (state, action) => {
      state.message = "bulk_delete_succeeded";
      ordersAdapter.removeMany(state, action.meta.arg.orderIds);
    });

    builder.addCase(doBulkUpdateOrders.fulfilled, state => {
      state.message = "bulk_update_succeeded";
    });
    builder.addCase(doBulkUpdateDeliveryDate.fulfilled, state => {
      state.message = "bulk_update_succeeded";
    });

    builder.addCase(doDetailOrder.pending, state => {
      state.action = "detail";
    });
    builder.addCase(doDetailOrder.fulfilled, (state, action) => {
      state.status = "succeeded";
      state.item = action.payload;
    });
    builder.addCase(doDetailOrder.rejected, state => {
      state.status = "failed";
    });

    builder.addCase(doDetailTrackingOrder.pending, state => {
      state.action = "detail";
    });
    builder.addCase(doDetailTrackingOrder.fulfilled, (state, action) => {
      state.status = "succeeded";
      state.item = action.payload;
    });
    builder.addCase(doDetailTrackingOrder.rejected, state => {
      state.status = "failed";
    });

    builder.addCase(doDetailPublicOrder.pending, state => {
      state.action = "detail";
    });
    builder.addCase(doDetailPublicOrder.fulfilled, (state, action) => {
      state.status = "succeeded";
      state.item = action.payload;
    });
    builder.addCase(doDetailPublicOrder.rejected, state => {
      state.status = "failed";
    });

    builder.addMatcher(isActionWithPending, state => {
      state.isFetching = true;
      state.message = null;
    });
    builder.addMatcher(isActionWithFulfilled, state => {
      state.isFetching = false;
    });
    builder.addMatcher(isActionWithFailed, state => {
      state.isFetching = false;
      state.message = null;
    });
  },
});

export const {
  selectAll: selectAllOrders,
  selectById: selectOrderById,
  selectIds: selectOrderIds,
} = ordersAdapter.getSelectors(state => state.order);

const { reducer: orderReducer } = orderSlice;
export default orderReducer;
